import { useState, useEffect } from 'react';
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import { RefreshCw, ExternalLink, MessageSquare, Users, Video, Check, AlertCircle } from 'lucide-react';
import { Skeleton } from '../ui/Skeleton';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { useCacheEvents } from '../../hooks/useCacheEvents';

interface TeamsNotification {
  id: string;
  chatId: string;
  messageId: string;
  from: {
    name: string;
    email: string;
  };
  subject: string;
  preview: string;
  timestamp: string;
  isRead: boolean;
  deepLink: string;
  chatType: 'oneOnOne' | 'group' | 'meeting';
}

interface Props {
  open: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
}

export function UserNotificationsDialog({ open, onClose, onUnreadCountChange }: Props) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<TeamsNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  useEffect(() => {
    if (open) {
      loadNotifications();
    }
  }, [open]);

  // If notifications cache refreshes (and this user is affected), update the open dialog.
  useCacheEvents({
    enabled: !!open && !!user?.email,
    cacheNames: ['user_notifications'],
    onEvent: (e) => {
      const email = String(user?.email || '').trim().toLowerCase();
      const affected = Array.isArray(e.affectedUsers) ? e.affectedUsers.map((x) => String(x).trim().toLowerCase()) : [];
      if (affected.length > 0 && email && !affected.includes(email)) return;
      // Reload from cache-backed endpoint (does not rely on polling).
      void (async () => {
        try {
          const data = await apiClient.getUserNotifications();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
          onUnreadCountChange?.(data.unreadCount || 0);
        } catch {
          // ignore
        }
      })();
    },
  });

  const loadNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      // First check if user has Graph API access
      const accessRes = await apiClient.checkUserGraphApiAccess();
      setHasAccess(accessRes.hasAccess);

      if (!accessRes.hasAccess) {
        setLoading(false);
        return;
      }

      // Fetch notifications
      const data = await apiClient.getUserNotifications();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
      onUnreadCountChange?.(data.unreadCount || 0);
      
      if (data.error) {
        setError(data.error);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const data = await apiClient.refreshUserNotifications();
      setNotifications(data.notifications || []);
      setUnreadCount(data.unreadCount || 0);
      onUnreadCountChange?.(data.unreadCount || 0);
      
      if (data.error) {
        setError(data.error);
      } else {
        setError(null);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to refresh notifications');
    } finally {
      setRefreshing(false);
    }
  };

  const handleNotificationClick = (notification: TeamsNotification) => {
    // Requirement: do NOT mark as read from portal; read state is managed by Teams.
    window.open(notification.deepLink, '_blank');
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getChatIcon = (chatType: string) => {
    switch (chatType) {
      case 'group':
        return <Users className="w-4 h-4" />;
      case 'meeting':
        return <Video className="w-4 h-4" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent maxWidth="lg">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Teams Notifications</DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing || loading}
              className="h-8 px-2"
            >
              <RefreshCw className={cn("w-4 h-4", refreshing && "animate-spin")} />
            </Button>
          </div>
        </DialogHeader>

        <DialogBody>
          {loading && (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-3 p-3">
                  <Skeleton className="w-10 h-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-full" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && hasAccess === false && (
            <div className="py-8 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Teams Integration Not Available
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                Graph API access has not been configured for your organization. 
                Contact your administrator to enable Teams notifications.
              </p>
            </div>
          )}

          {!loading && hasAccess && error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-800 dark:text-red-200 mb-4">
              {error}
            </div>
          )}

          {!loading && hasAccess && !error && notifications.length === 0 && (
            <div className="py-8 text-center">
              <MessageSquare className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                No Notifications
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                You're all caught up! No new Teams messages.
              </p>
            </div>
          )}

          {!loading && hasAccess && notifications.length > 0 && (
            <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-[60vh] overflow-y-auto">
              {notifications.filter((n) => !n.isRead).map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "w-full text-left p-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors",
                    !notification.isRead && "bg-blue-50/50 dark:bg-blue-900/20"
                  )}
                >
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                      notification.isRead 
                        ? "bg-gray-100 dark:bg-gray-800 text-gray-500" 
                        : "bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400"
                    )}>
                      {getChatIcon(notification.chatType)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={cn(
                          "text-sm truncate",
                          notification.isRead 
                            ? "text-gray-600 dark:text-gray-400" 
                            : "font-medium text-gray-900 dark:text-gray-100"
                        )}>
                          {notification.from.name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                          {formatTimestamp(notification.timestamp)}
                        </span>
                      </div>
                      <div className={cn(
                        "text-xs mt-0.5",
                        notification.isRead 
                          ? "text-gray-500 dark:text-gray-500" 
                          : "text-gray-700 dark:text-gray-300"
                      )}>
                        {notification.subject}
                      </div>
                      {notification.preview && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-4 whitespace-pre-line break-words">
                          {notification.preview}
                        </p>
                      )}
                    </div>

                    {/* Status indicators */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {!notification.isRead && (
                        <div className="w-2 h-2 rounded-full bg-blue-500" />
                      )}
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Footer with unread count */}
          {!loading && hasAccess && notifications.length > 0 && (
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
              <span>
                {unreadCount > 0 
                  ? `${unreadCount} unread message${unreadCount !== 1 ? 's' : ''}`
                  : 'All messages read'
                }
              </span>
              <span className="text-xs">
                Click to open in Teams
              </span>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
